import React, { useState, useMemo } from 'react';
import { StyleSheet, View } from 'react-native';
import { useAuthStore, useToastStore } from '@/store';
import { accountApi } from '@/lib/api';
import Button from '@/components/Buttons/Button';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import StatusContainer from '@/components/StatusContainer';
import useTranslation from '@/hooks/useTranslation';

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  inputWrapper: {
    gap: 8,
    width: '100%',
  },
});

export default function UserUpdatePasswordForm() {
  const { addToast } = useToastStore(state => state.actions);
  const { t } = useTranslation();
  const hasPassword = useAuthStore(state => state.user?.hasPassword);
  const fetchUser = useAuthStore(state => state.fetchUser);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [error, setError] = useState('');

  const isChangeDisabled = useMemo(() => {
    return isUpdatingPassword || !currentPassword || !newPassword || !confirmPassword;
  }, [isUpdatingPassword, currentPassword, newPassword, confirmPassword]);

  const isSetDisabled = useMemo(() => {
    return isUpdatingPassword || !newPassword || !confirmPassword;
  }, [isUpdatingPassword, newPassword, confirmPassword]);

  const handleUpdatePassword = async () => {
    try {
      setError('');
      if (!currentPassword || !newPassword || !confirmPassword) {
        setError(t('forms.password.fillAllFields'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('forms.password.passwordsNoMatch'));
        return;
      }
      setIsUpdatingPassword(true);
      await accountApi.changePassword(currentPassword, newPassword, confirmPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      addToast(t('forms.password.updatedSuccess'), 'success');
    } catch (e) {
      setError(e?.message || t('forms.password.updateFailed'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const handleSetPassword = async () => {
    try {
      setError('');
      if (!newPassword || !confirmPassword) {
        setError(t('forms.password.fillAllFields'));
        return;
      }
      if (newPassword !== confirmPassword) {
        setError(t('forms.password.passwordsNoMatch'));
        return;
      }
      setIsUpdatingPassword(true);
      await accountApi.setPassword(newPassword, confirmPassword);
      setNewPassword('');
      setConfirmPassword('');
      await fetchUser();
      addToast(t('forms.password.setSuccess'), 'success');
    } catch (e) {
      setError(e?.message || t('forms.password.setFailed'));
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!hasPassword) {
    return (
      <View style={styles.container}>
        <View style={styles.inputWrapper}>
          <StatusContainer type="empty" message={t('forms.password.ssoNoPassword')} />

          {error ? <StatusContainer type="error" message={error} /> : null}

          <FloatingLabelInput
            label={t('forms.password.newPassword')}
            value={newPassword}
            onChangeText={setNewPassword}
            placeholder={t('forms.password.newPassword')}
            secureTextEntry
            isFullWidth
          />

          <FloatingLabelInput
            label={t('forms.password.confirmNewPassword')}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            placeholder={t('forms.password.confirmNewPassword')}
            secureTextEntry
            isFullWidth
          />
        </View>

        <Button
          text={t('forms.password.setPassword')}
          variant={isSetDisabled ? 'secondary' : 'primary'}
          isLoading={isUpdatingPassword}
          isDisabled={isSetDisabled}
          onPress={handleSetPassword}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.inputWrapper}>
        {error ? <StatusContainer type="error" message={error} /> : null}

        <FloatingLabelInput
          label={t('forms.password.currentPassword')}
          value={currentPassword}
          onChangeText={setCurrentPassword}
          placeholder={t('forms.password.currentPassword')}
          secureTextEntry
          isFullWidth
        />
        
        <FloatingLabelInput
          label={t('forms.password.newPassword')}
          value={newPassword}
          onChangeText={setNewPassword}
          placeholder={t('forms.password.newPassword')}
          secureTextEntry
          isFullWidth
        />
        
        <FloatingLabelInput
          label={t('forms.password.confirmNewPassword')}
          value={confirmPassword}
          onChangeText={setConfirmPassword}
          placeholder={t('forms.password.confirmNewPassword')}
          secureTextEntry
          isFullWidth
        />
      </View>

      <Button
        text={t('forms.password.changePassword')}
        variant={isChangeDisabled ? 'secondary' : 'primary'}
        isLoading={isUpdatingPassword}
        isDisabled={isChangeDisabled}
        onPress={handleUpdatePassword}
        style={{ marginTop: 8 }}
      />
    </View>
  );
} 